import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Trash2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const decimalPayloadValue = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized === '' ? '0' : normalized;
};

const JournalEntries = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);
  const [form, setForm] = useState({ entry_date: new Date().toISOString().split('T')[0], description: '', reference_type: 'manual', lines: [{ account_id: '', description: '', debit_amount: '', credit_amount: '' }, { account_id: '', description: '', debit_amount: '', credit_amount: '' }] });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [je, accts] = await Promise.all([api.get(`/accounts/journal-entries${statusFilter ? `?status=${statusFilter}` : ''}`), api.get('/accounts/chart')]);
      setEntries(je.data); setAccounts(accts.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchData(); }, [statusFilter]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: '', description: '', debit_amount: '', credit_amount: '' }] });
  const removeLine = (i) => { if (form.lines.length > 2) setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) }); };
  const updateLine = (i, field, val) => { const l = [...form.lines]; l[i] = { ...l[i], [field]: val }; setForm({ ...form, lines: l }); };

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit_amount) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit_amount) || 0), 0);

  const handleSubmit = async (post) => {
    setFormError('');
    if (!form.description) { setFormError('Description required'); return; }
    if (form.lines.some(l => !l.account_id)) { setFormError('All lines need an account'); return; }
    if (Math.abs(totalDebit - totalCredit) > 0.01) { setFormError(`Debits (${totalDebit.toFixed(2)}) must equal Credits (${totalCredit.toFixed(2)})`); return; }
    if (totalDebit === 0) { setFormError('Total must be greater than 0'); return; }
    setFormLoading(true);
    try {
      await api.post('/accounts/journal-entries', {
        ...form,
        post,
        lines: form.lines.map(l => ({ account_id: l.account_id, description: l.description, debit_amount: decimalPayloadValue(l.debit_amount), credit_amount: decimalPayloadValue(l.credit_amount) })),
      });
      setShowForm(false); fetchData();
    } catch (err) { setFormError(err.response?.data?.detail || 'Failed'); } finally { setFormLoading(false); }
  };

  const handlePost = async (entry) => {
    if (!window.confirm(`Post ${entry.entry_number}? Account balances will be updated.`)) return;
    try { await api.post(`/accounts/journal-entries/${entry.id}/post`); fetchData(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleView = async (entry) => {
    try { const r = await api.get(`/accounts/journal-entries/${entry.id}`); setViewEntry(r.data); } catch { alert('Failed'); }
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="journal-entries-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="je-title">Journal Entries</h1>
            <button onClick={() => { setForm({ entry_date: new Date().toISOString().split('T')[0], description: '', reference_type: 'manual', lines: [{ account_id: '', description: '', debit_amount: '', credit_amount: '' }, { account_id: '', description: '', debit_amount: '', credit_amount: '' }] }); setShowForm(true); }} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium flex items-center gap-2" data-testid="add-je-button"><Plus className="w-5 h-5" /> New Entry</button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <select className="border border-[#e5e7eb] rounded-sm px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="je-status-filter">
              <option value="">All</option><option value="draft">Draft</option><option value="posted">Posted</option>
            </select>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : entries.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center"><p className="text-[#6b7280]">No journal entries</p></div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="je-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr><th className="py-3 px-4">Entry #</th><th className="py-3 px-4">Date</th><th className="py-3 px-4">Description</th><th className="py-3 px-4">Type</th><th className="py-3 px-4 text-right">Debit</th><th className="py-3 px-4 text-right">Credit</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Actions</th></tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-medium">{e.entry_number}</td>
                      <td className="py-3 px-4">{e.entry_date ? new Date(e.entry_date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 truncate max-w-[200px]">{e.description}</td>
                      <td className="py-3 px-4"><span className="text-xs bg-[#F5F5F5] px-2 py-0.5 rounded-sm">{e.reference_type}</span></td>
                      <td className="py-3 px-4 text-right font-medium">Rs. {parseFloat(e.total_debit).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-medium">Rs. {parseFloat(e.total_credit).toLocaleString()}</td>
                      <td className="py-3 px-4"><span className={`px-2 py-1 rounded-sm text-xs font-medium ${e.status === 'posted' ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#F5F5F5] text-[#6b7280]'}`}>{e.status}</span></td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button onClick={() => handleView(e)} className="text-[#1E40AF] text-xs font-medium"><Eye className="w-3 h-3" /></button>
                          {e.status === 'draft' && <button onClick={() => handlePost(e)} className="text-[#1E40AF] text-xs font-medium" data-testid={`post-je-${e.id}`}>Post</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="je-form-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center shrink-0">
              <h2 className="text-xl font-semibold">New Journal Entry</h2>
              <button onClick={() => setShowForm(false)} className="text-[#6b7280] hover:text-[#1a1a2e]">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {formError && <div className="p-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm text-[#EF4444] text-sm">{formError}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.entry_date} onChange={e => setForm({...form, entry_date: e.target.value})} data-testid="je-date" /></div>
                <div><label className="block text-sm font-medium mb-1">Description <span className="text-[#EF4444]">*</span></label><input type="text" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="je-description" /></div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">Lines</label>
                  <button type="button" onClick={addLine} className="text-[#1E40AF] text-sm font-medium" data-testid="je-add-line"><Plus className="w-4 h-4 inline" /> Add Line</button>
                </div>
                <div className="border border-[#e5e7eb] rounded-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f9fafb] text-[#6b7280]">
                      <tr><th className="py-2 px-3 text-left">Account</th><th className="py-2 px-3 text-left">Description</th><th className="py-2 px-3 text-right w-28">Debit</th><th className="py-2 px-3 text-right w-28">Credit</th><th className="py-2 px-3 w-10"></th></tr>
                    </thead>
                    <tbody>
                      {form.lines.map((l, i) => (
                        <tr key={i} className="border-t border-[#e5e7eb]">
                          <td className="py-2 px-3"><select className="w-full border border-[#e5e7eb] rounded-sm px-2 py-1 text-sm" value={l.account_id} onChange={e => updateLine(i, 'account_id', e.target.value)} data-testid={`je-line-acct-${i}`}><option value="">Select Account</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select></td>
                          <td className="py-2 px-3"><input type="text" className="w-full border border-[#e5e7eb] rounded-sm px-2 py-1 text-sm" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} /></td>
                          <td className="py-2 px-3"><input type="number" min="0" step="0.01" className="w-full border border-[#e5e7eb] rounded-sm px-2 py-1 text-sm text-right" value={l.debit_amount} onChange={e => updateLine(i, 'debit_amount', e.target.value)} data-testid={`je-line-debit-${i}`} /></td>
                          <td className="py-2 px-3"><input type="number" min="0" step="0.01" className="w-full border border-[#e5e7eb] rounded-sm px-2 py-1 text-sm text-right" value={l.credit_amount} onChange={e => updateLine(i, 'credit_amount', e.target.value)} data-testid={`je-line-credit-${i}`} /></td>
                          <td className="py-2 px-3">{form.lines.length > 2 && <button type="button" onClick={() => removeLine(i)} className="text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#f9fafb] font-semibold">
                      <tr>
                        <td className="py-2 px-3" colSpan="2">Totals</td>
                        <td className="py-2 px-3 text-right">Rs. {totalDebit.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right">Rs. {totalCredit.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {Math.abs(totalDebit - totalCredit) > 0.01 && totalDebit > 0 && <p className="text-[#EF4444] text-sm mt-1">Difference: Rs. {Math.abs(totalDebit - totalCredit).toFixed(2)}</p>}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#e5e7eb] flex justify-end gap-3 shrink-0 bg-white">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#e5e7eb] rounded-sm hover:bg-[#f3f4f6] font-medium">Cancel</button>
              <button onClick={() => handleSubmit(false)} disabled={formLoading} className="px-4 py-2 border border-[#1E40AF] text-[#1E40AF] rounded-sm hover:bg-[#EFF6FF] font-medium disabled:opacity-50" data-testid="save-je-draft">Save Draft</button>
              <button onClick={() => handleSubmit(true)} disabled={formLoading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm hover:opacity-90 font-medium disabled:opacity-50" data-testid="post-je-button">Post</button>
            </div>
          </div>
        </div>
      )}

      {viewEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="je-detail-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center shrink-0">
              <h2 className="text-xl font-semibold">{viewEntry.entry_number}</h2>
              <button onClick={() => setViewEntry(null)} className="text-[#6b7280]">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div><span className="font-medium">Date:</span> {viewEntry.entry_date ? new Date(viewEntry.entry_date).toLocaleDateString() : '-'}</div>
                <div><span className="font-medium">Status:</span> <span className={`px-2 py-1 rounded-sm text-xs font-medium ${viewEntry.status === 'posted' ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#F5F5F5] text-[#6b7280]'}`}>{viewEntry.status}</span></div>
                <div className="col-span-2"><span className="font-medium">Description:</span> {viewEntry.description}</div>
              </div>
              <table className="w-full text-sm border border-[#e5e7eb]">
                <thead className="bg-[#f9fafb]"><tr><th className="py-2 px-3 text-left">Account</th><th className="py-2 px-3 text-left">Description</th><th className="py-2 px-3 text-right">Debit</th><th className="py-2 px-3 text-right">Credit</th></tr></thead>
                <tbody>
                  {viewEntry.lines?.map((l, i) => (
                    <tr key={i} className="border-t border-[#e5e7eb]">
                      <td className="py-2 px-3 font-mono text-xs">{l.account_code} - {l.account_name}</td>
                      <td className="py-2 px-3">{l.description}</td>
                      <td className="py-2 px-3 text-right">{l.debit_amount > 0 ? `Rs. ${parseFloat(l.debit_amount).toLocaleString()}` : '-'}</td>
                      <td className="py-2 px-3 text-right">{l.credit_amount > 0 ? `Rs. ${parseFloat(l.credit_amount).toLocaleString()}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#f9fafb] font-semibold"><tr><td colSpan="2" className="py-2 px-3">Total</td><td className="py-2 px-3 text-right">Rs. {parseFloat(viewEntry.total_debit).toLocaleString()}</td><td className="py-2 px-3 text-right">Rs. {parseFloat(viewEntry.total_credit).toLocaleString()}</td></tr></tfoot>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-[#e5e7eb] flex justify-end shrink-0"><button onClick={() => setViewEntry(null)} className="px-4 py-2 border border-[#e5e7eb] rounded-sm hover:bg-[#f3f4f6] font-medium">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntries;
